import { Form } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AssetDetail,
  AssetService,
  type UpdateAssetPayload,
} from '~/app/services/asset/asset.service';
import { AssetTypeService } from '~/app/services/asset-type/asset-type.service';
import { SiteService } from '~/app/services/site/site.service';
import { PartnersService } from '~/app/services/partners/partners.service';
import { useAppToast } from '~/app/hooks/useAppToast';
import { useAtmmOptionsForAssetType } from '~/app/hooks/asset/useAtmmOptionsForAssetType';
import { assetSummaryQueryKey } from '~/app/hooks/asset/useAssetSummary';
import { useAssetCustomFields } from '~/app/hooks/asset/useAssetCustomFields';
import type { CustomFieldFormValues } from '~/app/components/asset-custom-fields/utils';
import type { EvseType } from '@evimo/types/enums/evse-type';

export type AssetDetailFormValues = {
  assetName: string;
  serialNumber: string;
  manufacturerId?: string;
  siteId: string;
  zoneId?: string;
  assetTypeId: string;
  assetModelId?: string;
  standard?: EvseType | null;
  installedAt?: Dayjs | null;
  commissionedAt?: Dayjs | null;
  decommissionedAt?: Dayjs | null;
  expectedDailyEarning?: number;
  landlordOrganizationId?: string | null;
  siteAddress?: string;
  siteLatitude?: string;
  siteLongitude?: string;
  customFields?: CustomFieldFormValues;
};

function normalizeFormValues(values: Partial<AssetDetailFormValues>) {
  const customFields: Record<string, string> = {};
  for (const [key, value] of Object.entries(values.customFields ?? {})) {
    if (dayjs.isDayjs(value)) {
      customFields[key] = value.toISOString();
    } else if (typeof value === 'boolean') {
      customFields[key] = value ? 'true' : 'false';
    } else if (value == null) {
      customFields[key] = '';
    } else {
      customFields[key] = String(value);
    }
  }

  return {
    assetName: values.assetName?.trim() ?? '',
    serialNumber: values.serialNumber?.trim() ?? '',
    manufacturerId: values.manufacturerId ?? '',
    siteId: values.siteId ?? '',
    zoneId: values.zoneId ?? '',
    assetTypeId: values.assetTypeId ?? '',
    assetModelId: values.assetModelId ?? '',
    standard: values.standard ?? '',
    installedAt: values.installedAt?.toISOString() ?? '',
    commissionedAt: values.commissionedAt?.toISOString() ?? '',
    decommissionedAt: values.decommissionedAt?.toISOString() ?? '',
    expectedDailyEarning: values.expectedDailyEarning ?? 0,
    landlordOrganizationId: values.landlordOrganizationId ?? '',
    siteAddress: values.siteAddress?.trim() ?? '',
    siteLatitude: values.siteLatitude?.trim() ?? '',
    siteLongitude: values.siteLongitude?.trim() ?? '',
    customFields,
  };
}

function toDateInput(value: string | null | undefined): Dayjs | null {
  return value ? dayjs(value) : null;
}

function toNullableIsoDate(value: Dayjs | null | undefined): string | null {
  return value ? value.toISOString() : null;
}

function toCoordinateInput(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function toNullableCoordinate(value: string | undefined): number | null {
  const normalizedValue = value?.trim() ?? '';

  if (!normalizedValue) {
    return null;
  }

  return Number(normalizedValue);
}

/** Shared React Query key for GET /api/v1/assets/details/:assetId (page + breadcrumb). */
export function assetDetailQueryKey(assetId: string) {
  return ['assets', 'detail', assetId] as const;
}

export function useAssetDetail(
  organizationId: string,
  assetId: string,
  options?: { enabled?: boolean },
) {
  const [form] = Form.useForm<AssetDetailFormValues>();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAppToast();
  const detailsEnabled =
    Boolean(assetId) && Boolean(organizationId) && (options?.enabled ?? true);

  const detailQuery = useQuery({
    queryKey: assetDetailQueryKey(assetId),
    queryFn: () => AssetService.findDetailById(organizationId, assetId),
    enabled: detailsEnabled,
    staleTime: Infinity,
  });

  const asset = detailQuery.data;
  const watchedValues = Form.useWatch([], form);
  const selectedSiteId = watchedValues?.siteId ?? asset?.site.id ?? '';
  const selectedAssetTypeId =
    watchedValues?.assetTypeId ?? asset?.assetType.id ?? '';
  const selectedAssetModelId =
    watchedValues?.assetModelId ?? asset?.assetModel?.id ?? '';

  const {
    fields: customFields,
    initialValues: customFieldInitialValues,
    isLoading: customFieldsLoading,
    saveCustomFieldValues,
    isSaving: customFieldsSaving,
  } = useAssetCustomFields({
    organizationId,
    assetTypeId: selectedAssetTypeId,
    assetId,
    enabled: detailsEnabled,
  });

  const assetTypesQuery = useQuery({
    queryKey: ['assets', 'detail', 'asset-types'],
    queryFn: () => AssetTypeService.findAll(),
    enabled: detailsEnabled,
  });

  const sitesQuery = useQuery({
    queryKey: ['assets', 'detail', 'sites', organizationId],
    queryFn: () => SiteService.findAllByOrganizationId(organizationId),
    enabled: detailsEnabled && !!organizationId,
  });

  const zonesQuery = useQuery({
    queryKey: ['assets', 'detail', 'zones', organizationId, selectedSiteId],
    queryFn: () =>
      SiteService.findZonesBySiteId(organizationId, selectedSiteId),
    enabled: detailsEnabled && !!selectedSiteId,
  });

  const {
    assetModelOptions,
    manufacturerOptions,
    isLoading: atmmLoading,
  } = useAtmmOptionsForAssetType(
    detailsEnabled ? selectedAssetTypeId : '',
    detailsEnabled ? selectedAssetModelId : '',
  );

  const landlordsQuery = useQuery({
    queryKey: ['assets', 'detail', 'landlords', organizationId],
    queryFn: () => PartnersService.listLandlords(organizationId),
    enabled: detailsEnabled && !!organizationId,
  });

  const sites = sitesQuery.data ?? [];

  const initialValues = asset
    ? (() => {
        const siteFromList = sites.find((site) => site.id === asset.site.id);

        return {
          assetName: asset.assetName,
          serialNumber: asset.serialNumber ?? '',
          manufacturerId: asset.manufacturerId ?? undefined,
          siteId: asset.site.id,
          zoneId: asset.zone?.id ?? undefined,
          assetTypeId: asset.assetType.id,
          assetModelId: asset.assetModel?.id ?? undefined,
          standard: asset.standard ?? undefined,
          installedAt: toDateInput(asset.installedAt),
          commissionedAt: toDateInput(asset.commissionedAt),
          decommissionedAt: toDateInput(asset.decommissionedAt),
          siteAddress: siteFromList?.address ?? asset.site.address ?? undefined,
          siteLatitude: toCoordinateInput(
            siteFromList?.latitude ?? asset.site.latitude,
          ),
          siteLongitude: toCoordinateInput(
            siteFromList?.longitude ?? asset.site.longitude,
          ),
          expectedDailyEarning: asset.expectedDailyEarning,
          landlordOrganizationId: asset.landlordOrganizationId ?? undefined,
          customFields: customFieldInitialValues,
        } satisfies AssetDetailFormValues;
      })()
    : null;

  useEffect(() => {
    if (!asset) {
      return;
    }

    const siteFromList = sites.find((site) => site.id === asset.site.id);

    form.setFieldsValue({
      assetName: asset.assetName,
      serialNumber: asset.serialNumber ?? '',
      manufacturerId: asset.manufacturerId ?? undefined,
      siteId: asset.site.id,
      zoneId: asset.zone?.id ?? undefined,
      assetTypeId: asset.assetType.id,
      assetModelId: asset.assetModel?.id ?? undefined,
      standard: asset.standard ?? undefined,
      installedAt: toDateInput(asset.installedAt),
      commissionedAt: toDateInput(asset.commissionedAt),
      decommissionedAt: toDateInput(asset.decommissionedAt),
      siteAddress: siteFromList?.address ?? asset.site.address ?? undefined,
      siteLatitude: toCoordinateInput(
        siteFromList?.latitude ?? asset.site.latitude,
      ),
      siteLongitude: toCoordinateInput(
        siteFromList?.longitude ?? asset.site.longitude,
      ),
      expectedDailyEarning: asset.expectedDailyEarning,
      landlordOrganizationId: asset.landlordOrganizationId ?? undefined,
    });
  }, [asset, form, sites]);

  useEffect(() => {
    if (customFieldsLoading) {
      return;
    }
    form.setFieldsValue({ customFields: customFieldInitialValues });
  }, [customFieldsLoading, customFieldInitialValues, form]);

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateAssetPayload) =>
      AssetService.updateById(organizationId, assetId, payload),
    onSuccess: (updatedAsset) => {
      queryClient.setQueryData(assetDetailQueryKey(assetId), updatedAsset);
      queryClient.invalidateQueries({ queryKey: ['assets', 'overview'] });
      queryClient.invalidateQueries({
        queryKey: assetSummaryQueryKey(assetId),
      });
    },
  });

  const updateSiteMutation = useMutation({
    mutationFn: ({
      siteId,
      address,
      latitude,
      longitude,
    }: {
      siteId: string;
      address: string | null;
      latitude: number | null;
      longitude: number | null;
    }) =>
      SiteService.updateById(organizationId, siteId, {
        address,
        latitude,
        longitude,
      }),
    onSuccess: (updatedSite, variables) => {
      queryClient.setQueryData<AssetDetail | undefined>(
        assetDetailQueryKey(assetId),
        (currentAsset) => {
          if (!currentAsset) {
            return currentAsset;
          }

          return {
            ...currentAsset,
            site:
              currentAsset.site.id === variables.siteId
                ? {
                    ...currentAsset.site,
                    address: updatedSite.address,
                    latitude: updatedSite.latitude,
                    longitude: updatedSite.longitude,
                  }
                : currentAsset.site,
          };
        },
      );

      queryClient.setQueryData(
        ['assets', 'detail', 'sites', organizationId],
        (
          currentSites:
            | Array<{
                id: string;
                name: string;
                address: string | null;
                latitude: number | null;
                longitude: number | null;
              }>
            | undefined,
        ) =>
          currentSites?.map((site) =>
            site.id === updatedSite.id
              ? {
                  ...site,
                  address: updatedSite.address,
                  latitude: updatedSite.latitude,
                  longitude: updatedSite.longitude,
                }
              : site,
          ) ?? currentSites,
      );

      queryClient.invalidateQueries({
        queryKey: assetSummaryQueryKey(assetId),
      });
    },
  });

  const handleReset = (): void => {
    if (asset) {
      form.setFieldsValue({
        assetName: asset.assetName,
        manufacturerId: asset.manufacturerId ?? undefined,
        siteId: asset.site.id,
        zoneId: asset.zone?.id ?? undefined,
        assetTypeId: asset.assetType.id,
        assetModelId: asset.assetModel?.id ?? undefined,
        standard: asset.standard ?? undefined,
        installedAt: toDateInput(asset.installedAt),
        commissionedAt: toDateInput(asset.commissionedAt),
        decommissionedAt: toDateInput(asset.decommissionedAt),
        siteAddress: asset.site.address ?? undefined,
        siteLatitude: toCoordinateInput(asset.site.latitude),
        siteLongitude: toCoordinateInput(asset.site.longitude),
        expectedDailyEarning: asset.expectedDailyEarning,
        landlordOrganizationId: asset.landlordOrganizationId ?? undefined,
        customFields: customFieldInitialValues,
      });
    }
  };

  const handleValuesChange = (
    changedValues: Partial<AssetDetailFormValues>,
  ): void => {
    if (changedValues.siteId !== undefined) {
      form.setFieldValue('zoneId', undefined);

      const selectedSite = sites.find(
        (site) => site.id === changedValues.siteId,
      );

      if (selectedSite) {
        form.setFieldsValue({
          siteAddress: selectedSite.address ?? undefined,
          siteLatitude: toCoordinateInput(selectedSite.latitude),
          siteLongitude: toCoordinateInput(selectedSite.longitude),
        });
      }
    }

    if (changedValues.assetTypeId !== undefined) {
      form.setFieldsValue({
        assetModelId: undefined,
        manufacturerId: undefined,
        customFields: {},
      });
    }

    if (changedValues.assetModelId !== undefined) {
      form.setFieldValue('manufacturerId', undefined);
    }
  };

  const handleCoordinatesChange = (coords: {
    lat: number;
    lng: number;
  }): void => {
    form.setFieldsValue({
      siteLatitude: String(coords.lat),
      siteLongitude: String(coords.lng),
    });
  };

  const handleSubmit = async (): Promise<void> => {
    const values = await form.validateFields();

    const nextAddress = values.siteAddress?.trim() || null;
    const nextLatitude = toNullableCoordinate(values.siteLatitude);
    const nextLongitude = toNullableCoordinate(values.siteLongitude);

    const shouldUpdateAsset =
      !asset ||
      values.assetName.trim() !== asset.assetName ||
      values.serialNumber.trim() !== (asset.serialNumber ?? '') ||
      (values.manufacturerId || '') !== (asset.manufacturerId ?? '') ||
      values.siteId !== asset.site.id ||
      (values.zoneId || '') !== (asset.zone?.id ?? '') ||
      values.assetTypeId !== asset.assetType.id ||
      (values.assetModelId || '') !== (asset.assetModel?.id ?? '') ||
      (values.standard ?? null) !== (asset.standard ?? null) ||
      toNullableIsoDate(values.installedAt) !== asset.installedAt ||
      toNullableIsoDate(values.commissionedAt) !== asset.commissionedAt ||
      toNullableIsoDate(values.decommissionedAt) !== asset.decommissionedAt ||
      (values.expectedDailyEarning ?? 0) !== asset.expectedDailyEarning ||
      (values.landlordOrganizationId ?? null) !== asset.landlordOrganizationId;

    const siteToUpdate =
      sites.find((site) => site.id === values.siteId) ??
      (asset && asset.site.id === values.siteId
        ? {
            id: asset.site.id,
            address: asset.site.address,
            latitude: asset.site.latitude,
            longitude: asset.site.longitude,
          }
        : null);

    const siteFieldsChanged =
      initialValues !== null &&
      ((values.siteAddress?.trim() ?? '') !==
        (initialValues.siteAddress?.trim() ?? '') ||
        (values.siteLatitude?.trim() ?? '') !==
          (initialValues.siteLatitude?.trim() ?? '') ||
        (values.siteLongitude?.trim() ?? '') !==
          (initialValues.siteLongitude?.trim() ?? ''));

    const shouldUpdateSite =
      siteFieldsChanged && !!values.siteId && !!siteToUpdate;

    const mutationTasks: Promise<unknown>[] = [];

    if (shouldUpdateAsset) {
      mutationTasks.push(
        updateMutation.mutateAsync({
          assetName: values.assetName.trim(),
          serialNumber: values.serialNumber.trim(),
          manufacturerId: values.manufacturerId || null,
          siteId: values.siteId,
          zoneId: values.zoneId || null,
          assetTypeId: values.assetTypeId,
          assetModelId: values.assetModelId || null,
          standard: values.standard || null,
          installedAt: toNullableIsoDate(values.installedAt),
          commissionedAt: toNullableIsoDate(values.commissionedAt),
          decommissionedAt: toNullableIsoDate(values.decommissionedAt),
          expectedDailyEarning: values.expectedDailyEarning ?? 0,
          landlordOrganizationId: values.landlordOrganizationId ?? null,
        }),
      );
    }

    if (shouldUpdateSite && siteToUpdate) {
      mutationTasks.push(
        updateSiteMutation.mutateAsync({
          siteId: siteToUpdate.id,
          address: nextAddress,
          latitude: nextLatitude,
          longitude: nextLongitude,
        }),
      );
    }

    if (customFields.length > 0) {
      mutationTasks.push(
        saveCustomFieldValues({
          organizationId,
          assetId,
          formValues: values.customFields ?? {},
        }),
      );
    }

    try {
      await Promise.all(mutationTasks);
      if (mutationTasks.length > 0) {
        showSuccess('notifications.saved', 'Changes saved successfully');
      }
    } catch (err) {
      showError(
        'errors.unexpectedRequest',
        'An error occurred while saving changes',
      );
      throw err;
    }
  };

  const hasChanges =
    initialValues !== null &&
    JSON.stringify(normalizeFormValues(initialValues)) !==
      JSON.stringify(normalizeFormValues(watchedValues ?? {}));

  const siteForMap =
    sites.find((site) => site.id === selectedSiteId) ??
    (asset
      ? {
          id: asset.site.id,
          name: asset.site.name,
          address: asset.site.address,
          latitude: asset.site.latitude,
          longitude: asset.site.longitude,
        }
      : null);

  const landlordOptions = (landlordsQuery.data?.items ?? []).map((item) => ({
    value: item.partnerOrgId,
    label: item.partnerName,
  }));

  return {
    asset,
    assetModels: assetModelOptions.map((option) => ({
      id: option.value,
      name: option.label,
    })),
    manufacturerOptions,
    atmmLoading,
    assetTypes: assetTypesQuery.data ?? [],
    error:
      detailQuery.error ??
      assetTypesQuery.error ??
      sitesQuery.error ??
      zonesQuery.error ??
      updateMutation.error ??
      updateSiteMutation.error ??
      null,
    form,
    handleReset,
    handleSubmit,
    handleValuesChange,
    handleCoordinatesChange,
    hasChanges,
    isLoading: detailQuery.isLoading,
    isSaving:
      updateMutation.isPending ||
      updateSiteMutation.isPending ||
      customFieldsSaving,
    landlordOptions,
    siteForMap,
    sites,
    zones: zonesQuery.data ?? [],
    customFields,
    customFieldsLoading,
  };
}
